import client from './client';
import type { Waiting } from '../types/waiting';

interface CallResponse {
  success: boolean;
  channel: string;
  phone: string;
}

export async function getWaitings(storeId: string, date?: string): Promise<Waiting[]> {
  const params = date ? { date } : undefined;
  const response = await client.get<{ waitings: Waiting[] } | Waiting[]>(
    `/api/stores/${storeId}/waitings`,
    { params },
  );

  const data = response.data;

  // 응답이 { waitings: [...] } 또는 [...] 형태일 수 있음
  if (Array.isArray(data)) {
    return data;
  }
  if (data && 'waitings' in data) {
    return data.waitings;
  }
  return [];
}

export async function callWaiting(
  waitingId: string,
): Promise<CallResponse> {
  const response = await client.post<CallResponse>(
    `/api/waitings/${waitingId}/call`,
  );
  return response.data;
}

export async function seatWaiting(waitingId: string): Promise<void> {
  await client.post(`/api/waitings/${waitingId}/seat`);
}

export async function noShowWaiting(waitingId: string): Promise<void> {
  await client.post(`/api/waitings/${waitingId}/no-show`);
}

export async function cancelWaiting(waitingId: string): Promise<void> {
  await client.post(`/api/waitings/${waitingId}/cancel`);
}

// --- 매장 설정 API ---

export interface StoreSettings {
  businessHours?: {
    open?: string;
    close?: string;
    lastOrder?: string;
  };
  isWaitingOpen?: boolean;
  waitingClosedMessage?: string;
  averageMinutesPerTeam?: number;
  takeoutEnabled?: boolean;
  averageMinutesPerTakeout?: number;
  /** 먹킷 등 외부 앱에서 원격 줄서기 등록 허용 (default true) */
  remoteWaitingEnabled?: boolean;
  sendRegistrationNotification?: boolean;
  sendPreCallNotification?: boolean;
  preCallTeamsBefore?: number;
  [key: string]: unknown;
}

export async function getStoreSettings(storeId: string): Promise<StoreSettings> {
  const response = await client.get<StoreSettings>(
    `/api/stores/${storeId}/settings`,
  );
  return response.data;
}

export async function updateStoreSettings(
  storeId: string,
  settings: Partial<StoreSettings>,
): Promise<StoreSettings> {
  const response = await client.patch<StoreSettings>(
    `/api/stores/${storeId}/settings`,
    { settings },
  );
  return response.data;
}

export async function toggleWaiting(
  storeId: string,
): Promise<{ isWaitingOpen: boolean }> {
  const response = await client.post<{ isWaitingOpen: boolean }>(
    `/api/stores/${storeId}/waiting-toggle`,
  );
  return response.data;
}

// --- 매장 이미지 API ---

export async function getStoreImage(storeId: string): Promise<{ storeImage: string | null }> {
  const res = await client.get<{ storeImage: string | null }>(`/api/stores/${storeId}/image`);
  return res.data;
}

export async function updateStoreImage(
  storeId: string,
  storeImage: string | null,
): Promise<{ storeImage: string | null }> {
  const res = await client.patch<{ storeImage: string | null }>(
    `/api/stores/${storeId}/image`,
    { storeImage },
  );
  return res.data;
}
